import type {Location} from 'history'
import hash from 'object-hash'
import type {RouteMatch, RouteObject} from 'react-router'
import {matchRoutes} from 'react-router'

import {browserHistory, Routes} from '../routes'
import {buildTabPredefined, buildTypeTabPredefined, projectTabPredefined} from '../selectors'
import {stringifyId} from '../types'
import type {TabId} from '../types'
import {base_uri} from '../types/BS_types'
import {objectEntries} from '../utils/object'
import type {KeyValue, WritableKeyValue} from '../utils/object'
import {safeLocalStorage} from '../utils/safeStorages'
import {generateUID} from '../utils/uid'

import type {FusAction, FusContext} from './statistics.types'

const SALT_KEY = 'trackingSalt'

const SALT = (function getSalt() {
  let salt = safeLocalStorage.getItem(SALT_KEY)

  if (salt == null) {
    salt = generateUID()
    safeLocalStorage.setItem(SALT_KEY, salt)
  }

  return salt
})()

const hashEntity = (name: string | null | undefined) =>
  (typeof name === 'string' && hash.sha1(`${SALT}:${name}`)) || ''

type RouteWithKey = RouteObject & {
  readonly key: string
}
const paths = objectEntries(Routes)
  .filter(([key]) => key !== 'BASE')
  .map(([key, value]) => ({
    path: value,
    key,
  }))

const getRoute = (location: Location) =>
  (window.location.origin + location.pathname).replace(`${base_uri}/`, '')

export const getEntity = (location: Location): RouteMatch | null | undefined =>
  matchRoutes(paths, getRoute(location))?.[0]

const getHashedParams = (params: KeyValue<string, string>) => {
  const result: WritableKeyValue<string, string> = {}

  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      result[key] = hashEntity(params[key])
    }
  }

  return result
}

export const getSecuredEntity = (entity: RouteMatch | null | undefined): FusContext => {
  if (!entity) {
    return {
      type: null,
    }
  }

  return {
    type: (entity.route as RouteWithKey).key,
    ...getHashedParams(entity.params),
  }
}

function retrieveContext(): FusContext {
  const entity = getEntity(browserHistory.location)
  return getSecuredEntity(entity)
}

function retrieveReferrer() {
  const referrer = document.referrer

  if (referrer === '') {
    return 'direct'
  }

  if (referrer.indexOf('/login.html')) {
    return 'login'
  }

  if (referrer.includes(base_uri)) {
    return 'internal'
  }

  return 'external'
}

export function retrieveTabContext(tab: TabId | null | undefined): string {
  if (!tab) {
    return 'default'
  }

  return projectTabPredefined(tab) || buildTypeTabPredefined(tab) || buildTabPredefined(tab)
    ? stringifyId(tab)
    : `custom:${hashEntity(stringifyId(tab))}`
}
export function retrieveLength(data: any): number {
  return Object.keys(data).length
}
const DEFAULT_ACTION_GROUP = 'teamcity.actions'
const DEFAULT_ACTION_GROUP_VERSION = '1'
const DEFAULT_ACTION_RECORDER_VERSION = '0.0.1'
export const ACTION_PAGES_GROUP = 'teamcity.pages'
export const ACTION_PAGES_VERSION = '1'
export const ACTION_PAGES_RECORDER_VERSION = '2'
const metaData = {
  group: DEFAULT_ACTION_GROUP,
  groupVersion: DEFAULT_ACTION_GROUP_VERSION,
  recorderVersion: DEFAULT_ACTION_RECORDER_VERSION,
}
export const generateFUSEvent = (
  action: string,
  payload?:
    | {
        context?: FusContext
        [key: string]: any
      }
    | null
    | undefined,
  options?: {
    withContext?: boolean
    withReferrer?: boolean
  },
): FusAction => {
  const event: FusAction = {
    count: 1,
    action,
    ...metaData,
    payload: payload ?? {},
  }

  if (options?.withContext === true) {
    event.payload.context = retrieveContext()
  }

  if (options?.withReferrer === true) {
    event.payload.referrer = retrieveReferrer()
  }

  return event
}
